import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ref, get, onValue, set } from 'firebase/database';
import { database } from '../firebaseConfig';
import { fetchScreenshotsByIds, updateScreenshot } from './supabaseConfig';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import './SharedIdeaList.css';

// Votes live in Firebase under shared-likes (works signed-out). like_count on
// the image record is only a fallback: anonymous visitors cannot write it.
const likeTotal = (image, likes) => {
    const id = image?.id;
    const fromVotes = likes?.[id] ?? likes?.[String(id)];
    if (typeof fromVotes === 'number') return fromVotes;
    return image?.like_count || 0;
};

const uploadedTime = (image) => {
    const direct = image?.uploadedAt ?? image?.createdAt ?? image?.created_at;
    if (typeof direct === 'number' && Number.isFinite(direct)) return direct;
    if (typeof direct === 'string') {
        const parsed = Date.parse(direct);
        if (!Number.isNaN(parsed)) return parsed;
    }
    const fromId = String(image?.id || '').match(/^uploaded_(\d{10,})_/);
    if (fromId) return Number(fromId[1]);
    return 0;
};

// Rated images first, highest score at the top. Images with no votes stay
// underneath those, newest upload first.
export const sortByLikes = (images, likes) => {
    return images
        .map((image, index) => ({ image, index }))
        .sort((a, b) => {
            const diff = likeTotal(b.image, likes) - likeTotal(a.image, likes);
            if (diff !== 0) return diff;
            if (likeTotal(a.image, likes) === 0) {
                const newer = uploadedTime(b.image) - uploadedTime(a.image);
                if (newer !== 0) return newer;
            }
            return a.index - b.index;
        })
        .map(({ image }) => image);
};

const SharedIdeaList = () => {
    const { shareToken } = useParams();
    const [listData, setListData] = useState(null);
    const [scenes, setScenes] = useState({});
    const [sceneImages, setSceneImages] = useState({});
    const [activeScene, setActiveScene] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [likes, setLikes] = useState({});
    const [likesReady, setLikesReady] = useState(false);
    const [likedImages, setLikedImages] = useState(new Set());
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [userId, setUserId] = useState(null);
    const [animatingImages, setAnimatingImages] = useState(new Set());
    const [lightboxImage, setLightboxImage] = useState(null);


    // Generate or retrieve unique user ID
    useEffect(() => {
        let storedUserId = localStorage.getItem('bonusclub_user_id');
        if (!storedUserId) {
            // Generate unique ID based on timestamp + random
            storedUserId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('bonusclub_user_id', storedUserId);
        }
        setUserId(storedUserId);
    }, []);

    useEffect(() => {
        let unsubscribeFn = null;

        const loadSharedList = async () => {
            try {
                const shareRef = ref(database, `shared-lists/${shareToken}`);
                const shareSnapshot = await get(shareRef);
                
                if (!shareSnapshot.exists()) {
                    setError('Shared list not found or expired');
                    setLoading(false);
                    return;
                }
                
                const shareData = shareSnapshot.val();
                
                if (shareData.expiresAt && shareData.expiresAt < Date.now()) {
                    setError('This shared link has expired');
                    setLoading(false);
                    return;
                }
                
                setListData({
                    name: shareData.listName,
                    description: shareData.description || '',
                    createdBy: shareData.createdBy || 'Unknown',
                    createdAt: shareData.createdAt,
                    isLive: shareData.isLive || false
                });
                
                if (shareData.isLive && shareData.userId && shareData.listId) {
                    const liveListRef = ref(database, `stories/${shareData.userId}/ideas/${shareData.listId}`);
                    
                    unsubscribeFn = onValue(liveListRef, async (snapshot) => {
                        if (snapshot.exists()) {
                            const liveListData = snapshot.val();
                            
                            if (!liveListData.isPublic) {
                                setError('This list is no longer public');
                                setLoading(false);
                                return;
                            }
                            
                            setListData(prev => ({
                                ...prev,
                                name: liveListData.name
                            }));

                            const boardScenes = liveListData.scenes || {};
                            setScenes(boardScenes);

                            const ownerUserId = shareData.userId;
                            const loadedSceneImages = {};

                            for (const sceneId of Object.keys(boardScenes)) {
                                const scene = boardScenes[sceneId];
                                const supabaseIds = scene.imageIds || [];
                                const uploadedIds = scene.uploadedImages || [];
                                let sceneImgs = [];

                                if (supabaseIds.length > 0) {
                                    try {
                                        const data = await fetchScreenshotsByIds(supabaseIds);
                                        sceneImgs.push(...supabaseIds.map(id => data.find(img => img.id === id)).filter(Boolean).map(img => ({ ...img, source: 'supabase' })));
                                    } catch (e) { console.error('Error loading supabase images:', e); }
                                }

                                if (uploadedIds.length > 0) {
                                    try {
                                        const promises = uploadedIds.map(async (imageId) => {
                                            const snap = await get(ref(database, `stories/${ownerUserId}/uploaded-images/${imageId}`));
                                            if (snap.exists()) {
                                                return { ...snap.val(), source: 'firebase', supabase_storage_url: snap.val().firebase_storage_url };
                                            }
                                            return null;
                                        });
                                        sceneImgs.push(...(await Promise.all(promises)).filter(Boolean));
                                    } catch (e) { console.error('Error loading uploaded images:', e); }
                                }

                                loadedSceneImages[sceneId] = sceneImgs;
                            }

                            setSceneImages(loadedSceneImages);
                            setLoading(false);
                        } else {
                            setError('Original list no longer exists');
                            setLoading(false);
                        }
                    }, (err) => {
                        console.error('Error listening to list updates:', err);
                        setError('Failed to load list updates');
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error loading shared list:', error);
                setError('Failed to load shared list');
                setLoading(false);
            }
        };
        
        if (shareToken) {
            loadSharedList();
        }

        return () => { if (unsubscribeFn) unsubscribeFn(); };
    }, [shareToken]);

    // Load likes data. Ranking waits for this so a refresh does not paint the
    // upload order and then freeze it before the votes arrive.
    useEffect(() => {
        let cancelled = false;

        const loadLikes = async () => {
            try {
                const likesRef = ref(database, `shared-likes/${shareToken}`);
                const likesSnapshot = await get(likesRef);
                
                if (cancelled) return;

                if (likesSnapshot.exists()) {
                    const likesData = likesSnapshot.val();
                    const totalLikes = {};
                    const userLikedImages = new Set();
                    
                    Object.keys(likesData).forEach(imageId => {
                        if (typeof likesData[imageId] === 'object') {
                            totalLikes[imageId] = Object.keys(likesData[imageId]).length;
                            if (userId && likesData[imageId][userId]) {
                                userLikedImages.add(imageId);
                            }
                        }
                    });
                    
                    setLikes(totalLikes);
                    setLikedImages(userLikedImages);
                } else {
                    setLikes({});
                    setLikedImages(new Set());
                }
            } catch (error) {
                console.error('Error loading likes:', error);
            } finally {
                if (!cancelled) setLikesReady(true);
            }
        };

        if (shareToken && userId) {
            setLikesReady(false);
            loadLikes();
        }

        return () => { cancelled = true; };
    }, [shareToken, userId]);

    const sceneKeys = Object.keys(scenes).sort((a, b) => (scenes[a].createdAt || 0) - (scenes[b].createdAt || 0));

    const getAllImages = () => {
        return sceneKeys.flatMap((sceneId) => sceneImages[sceneId] || []);
    };

    const getDisplayImages = () => {
        const images = activeScene === 'all' ? getAllImages() : (sceneImages[activeScene] || []);
        return sortByLikes(images, likes);
    };

    const totalImageCount = getAllImages().length;

    const navigateLightbox = useCallback((direction) => {
        if (!lightboxImage) return;
        const currentImages = getDisplayImages();
        const currentIndex = currentImages.findIndex(img => img.id === lightboxImage.id);
        if (currentIndex === -1) return;
        const nextIndex = currentIndex + direction;
        if (nextIndex >= 0 && nextIndex < currentImages.length) {
            setLightboxImage(currentImages[nextIndex]);
        }
    }, [lightboxImage, sceneImages, scenes, activeScene, likes]);

    useEffect(() => {
        if (!lightboxImage) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setLightboxImage(null);
            if (e.key === 'ArrowLeft') navigateLightbox(-1);
            if (e.key === 'ArrowRight') navigateLightbox(1);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [lightboxImage, navigateLightbox]);

    const showToastNotification = (message) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => {
            setShowToast(false);
        }, 2000);
    };

    const handleLike = async (imageId) => {
        if (!userId) return;
        
        try {
            const isLiked = likedImages.has(imageId);
            const newLikedImages = new Set(likedImages);
            
            if (isLiked) {
                newLikedImages.delete(imageId);
            } else {
                newLikedImages.add(imageId);
                // Add animation state for liked image
                setAnimatingImages(prev => new Set([...prev, imageId]));
            }
            
            setLikedImages(newLikedImages);
            
            // Update user's like status in Firebase
            const userLikeRef = ref(database, `shared-likes/${shareToken}/${imageId}/${userId}`);
            
            if (isLiked) {
                // Remove user's like
                await set(userLikeRef, null);
            } else {
                // Add user's like with timestamp
                await set(userLikeRef, {
                    liked: true,
                    timestamp: Date.now()
                });
            }
            
            // Calculate new total likes count
            const allLikesRef = ref(database, `shared-likes/${shareToken}/${imageId}`);
            const allLikesSnapshot = await get(allLikesRef);
            
            let newCount = 0;
            if (allLikesSnapshot.exists()) {
                const userLikes = allLikesSnapshot.val();
                newCount = Object.keys(userLikes).length;
            }
            
            try {
                await updateScreenshot(imageId, { like_count: newCount });
            } catch (apiError) {
                console.error('Error updating image like count:', apiError);
            }
            
            // Update local state
            setLikes(prev => ({
                ...prev,
                [imageId]: newCount
            }));
            
            // Show toast notification
            if (isLiked) {
                showToastNotification('💔 Removed from favorites');
            } else {
                showToastNotification('❤️ Added to favorites!');
                
                // Remove animation state after a short delay
                setTimeout(() => {
                    setAnimatingImages(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(imageId);
                        return newSet;
                    });
                }, 600);
            }
            
        } catch (error) {
            console.error('Error updating likes:', error);
            showToastNotification('⚠️ Something went wrong');
        }
    };



    if (loading || (!error && !likesReady)) {
        return (
            <div className="share-container">
                <div className="share-loading">
                    <div className="share-spinner-circle"></div>
                    <p>Loading images...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="share-container">
                <div className="share-error">
                    <h2>Oops!</h2>
                    <p>{error}</p>
                    <a href="/" className="share-back-button">Go to BonusClub</a>
                </div>
            </div>
        );
    }

    const displayImages = getDisplayImages();

    return (
        <div className="share-container">
            <div className="share-header">
                <div className="share-branding">
                    <div className="share-bonusclub-logo-container">
                        <img src="/img/BonusClub_Logo.webp" alt="BonusClub" className="share-bonusclub-logo" />
                    </div>
                </div>
                <div className="share-info">
                    <h2>{listData.name}</h2>
                    {listData.description && <p className="share-list-description">{listData.description}</p>}
                    <div className="share-list-meta">
                        <span>Shared by {listData.createdBy}</span>
                        <span>•</span>
                        <span>{totalImageCount} images</span>
                        <span>•</span>
                        <span>{sceneKeys.length} {sceneKeys.length === 1 ? 'scene' : 'scenes'}</span>
                        {listData.createdAt && (
                            <>
                                <span>•</span>
                                <span>{new Date(listData.createdAt).toLocaleDateString()}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Scene Navigation */}
            {sceneKeys.length > 0 && (
                <div className="share-scene-nav">
                    <button
                        className={`share-scene-tab ${activeScene === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveScene('all')}
                    >
                        All
                        <span className="share-scene-tab-count">{totalImageCount}</span>
                    </button>
                    {sceneKeys.map(sceneId => {
                        const scene = scenes[sceneId];
                        const imgCount = (sceneImages[sceneId] || []).length;
                        return (
                            <button
                                key={sceneId}
                                className={`share-scene-tab ${activeScene === sceneId ? 'active' : ''}`}
                                onClick={() => setActiveScene(sceneId)}
                            >
                                {scene.name}
                                <span className="share-scene-tab-count">{imgCount}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Scene description */}
            {activeScene !== 'all' && scenes[activeScene]?.description && (
                <div className="share-scene-description">
                    <p>{scenes[activeScene].description}</p>
                </div>
            )}

            {displayImages.length > 0 ? (
                <div className="share-images-masonry-container">
                    <ResponsiveMasonry
                        columnsCountBreakPoints={{
                            350: 1,
                            750: 2,
                            900: 2
                        }}
                    >
                        <Masonry gutter="24px">
                            {displayImages.map((image) => (
                                <div 
                                    key={image.id} 
                                    className={`share-image-container ${animatingImages.has(image.id) ? 'share-image-liked-animation' : ''}`}
                                >
                                    <img 
                                        src={image.supabase_storage_url || image.url} 
                                        alt={`Image ${image.id}`}
                                        loading="lazy"
                                        onClick={() => setLightboxImage(image)}
                                        style={{
                                            width: '100%',
                                            display: 'block',
                                            borderRadius: '12px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <button
                                        className={`share-like-button ${likedImages.has(image.id) ? 'share-liked' : ''}`}
                                        onClick={() => handleLike(image.id)}
                                        title="Like this image"
                                    >
                                        <svg className="share-heart-icon" viewBox="0 0 24 24" fill="none">
                                            <path 
                                                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" 
                                                stroke="currentColor" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                                fill={likedImages.has(image.id) ? "currentColor" : "none"}
                                            />
                                        </svg>
                                        <span className="share-like-count">
                                            {likes[image.id] || image.like_count || 0}
                                        </span>
                                    </button>
                                </div>
                            ))}
                        </Masonry>
                    </ResponsiveMasonry>
                </div>
            ) : (
                <div className="share-empty">
                    <p>{activeScene === 'all' ? 'No images in this board' : 'No images in this scene'}</p>
                </div>
            )}

            {/* Lightbox */}
            {lightboxImage && (() => {
                const currentImages = displayImages;
                const currentIndex = currentImages.findIndex(img => img.id === lightboxImage.id);
                const hasPrev = currentIndex > 0;
                const hasNext = currentIndex < currentImages.length - 1;

                return (
                    <div className="share-lightbox-overlay" onClick={() => setLightboxImage(null)}>
                        <button className="share-lightbox-close" onClick={() => setLightboxImage(null)}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>

                        {hasPrev && (
                            <button className="share-lightbox-nav share-lightbox-prev" onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                            </button>
                        )}

                        <img
                            src={lightboxImage.supabase_storage_url || lightboxImage.url}
                            alt={lightboxImage.description || lightboxImage.filename}
                            className="share-lightbox-image"
                            onClick={(e) => e.stopPropagation()}
                        />

                        {hasNext && (
                            <button className="share-lightbox-nav share-lightbox-next" onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </button>
                        )}

                        <div className="share-lightbox-counter" onClick={(e) => e.stopPropagation()}>
                            {currentIndex + 1} / {currentImages.length}
                        </div>
                    </div>
                );
            })()}

            {/* Toast Notification */}
            {showToast && (
                <div className={`share-toast-notification ${showToast ? 'share-show' : ''}`}>
                    {toastMessage}
                </div>
            )}

        </div>
    );
};

export default SharedIdeaList; 